import {
  Box,
  Button,
  ButtonGroup,
  HStack,
  Icon,
  Image,
  Spacer,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { t } from "i18next";
import { MapPageView } from "../../../../survey/stores/MapStore";
import { useStores } from "../../../../shared/stores/RootStore";
import logo from "@keita/lanepatrol-sdk/assets/logo/logo-vertical.png";
import {
  ArrowRightIcon,
  ExportIcon,
  MapIcon,
  StatsIcon,
  ImportIcon,
  ChevronDownIcon,
  CheckIcon,
} from "../../../../shared/views/icons";
import { CsvExportModal } from "./CsvExportModal";
import { Link, useNavigate } from "react-router-dom";
import { appRoutes } from "../../../../../App";
import { CsvImportModal } from "./CsvImportModal";
import { CustomMenu } from "../../../../shared/views/CustomMenu";

const HeaderBase: React.FC<{ isHome: boolean }> = ({ isHome }) => {
  const {
    authStore,
    mapStore,
    surveyStore,
    craNodeStore,
    craWayStore,
    hlaStore,
  } = useStores();
  const navigate = useNavigate();
  const exportModal = useDisclosure();
  const importModal = useDisclosure();
  const selectedCraNode = craNodeStore.getSelected();
  const selectedCraWay = craWayStore.getSelected();
  const selectedSurvey = surveyStore.getSelected();
  const selectedHlaWay = hlaStore.getSelected();
  const nodes = selectedCraWay
    ? craNodeStore.nodesByWayId.get(selectedCraWay.id)
    : undefined;

  const surveyIsClickable = !!selectedCraWay || !!selectedHlaWay;

  return (
    <>
      <HStack gap={4} minHeight={12} px={4} flexGrow={1}>
        <HStack
          gap={4}
          separator={<Icon as={ArrowRightIcon} borderInline={"none"} />}
        >
          <Link to={appRoutes.home()}>
            <Image height="40px" src={logo} alt="Logo" />
          </Link>

          {!!selectedSurvey && (
            <>
              <CustomMenu
                items={surveyStore.surveys
                  .filter((s) => s.city === selectedSurvey.city)
                  .map((survey) => ({
                    text: survey.name,
                    key: survey.id,
                    icon:
                      survey.id === selectedSurvey.id ? (
                        <Icon as={CheckIcon} color="primary.600" />
                      ) : (
                        <Box w={3.5} />
                      ),
                    onClick: () => {
                      navigate(appRoutes.survey(survey.id));
                    },
                  }))}
              >
                <Button
                  colorPalette={"primary"}
                  size="xs"
                  variant={"ghost"}
                  fontSize={"sm"}
                >
                  {selectedSurvey.city} <ChevronDownIcon />
                </Button>
              </CustomMenu>
              <Text
                fontSize="sm"
                fontWeight="semibold"
                cursor={surveyIsClickable ? "pointer" : "unset"}
                onClick={() => {
                  if (!surveyIsClickable) return;

                  craWayStore.clearSelected();
                  hlaStore.clearSelected();
                  mapStore.recenterMap();
                }}
              >
                {selectedSurvey.name}
              </Text>
            </>
          )}

          {!!selectedCraWay && (
            <Text
              fontSize="sm"
              fontWeight="semibold"
              cursor={selectedCraNode ? "pointer" : "unset"}
              onClick={() => {
                if (!selectedCraNode) return;
                craNodeStore.clearSelected();
                mapStore.recenterMap();
              }}
            >
              {selectedCraWay.name}
            </Text>
          )}

          {!!selectedCraNode && !!nodes && (
            <Text fontSize="sm" fontWeight="semibold">
              {t("map:nodeSummary.title", {
                position: nodes.indexOf(selectedCraNode) + 1,
                total: nodes.length,
              })}
            </Text>
          )}

          {!!selectedHlaWay && (
            <Text fontSize="sm" fontWeight="semibold">
              {selectedHlaWay.name ?? "Unknown street"}
            </Text>
          )}
        </HStack>

        <Spacer />

        {!isHome && (
          <ButtonGroup
            colorPalette="primary"
            size="sm"
            variant="ghost"
            fontSize={"sm"}
          >
            <Button
              onClick={
                mapStore.view === MapPageView.Dashboard
                  ? () => mapStore.setView(MapPageView.Map)
                  : () => mapStore.setView(MapPageView.Dashboard)
              }
            >
              <Icon
                as={
                  mapStore.view === MapPageView.Dashboard ? MapIcon : StatsIcon
                }
              />
              {mapStore.view === MapPageView.Dashboard
                ? t(`map:header.viewMapBtn`)
                : t(`map:header.viewDashboardBtn`)}
            </Button>

            {selectedSurvey?.permissions?.canManage && (
              <Button onClick={exportModal.onOpen}>
                <Icon as={ExportIcon} /> {t(`map:header.export`)}
              </Button>
            )}
            {authStore.isSuperAdmin && (
              <Button onClick={importModal.onOpen}>
                <Icon as={ImportIcon} /> Import data
              </Button>
            )}
          </ButtonGroup>
        )}
      </HStack>

      {exportModal.open && <CsvExportModal onClose={exportModal.onClose} />}
      {importModal.open && <CsvImportModal onClose={importModal.onClose} />}
    </>
  );
};

export const Header = observer(HeaderBase);
